import { Card, Col, Heading1, Margin, P, Row } from '@ovotech/nebula';
import { useAccountContext } from '@ovotech/ui-tools';
import React, { FormEvent, memo, useCallback, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { ErrorPanel } from './ErrorPanel';
import { LoadingPanel } from './LoadingPanel';
import { ProjectedBalance } from './ProjectedBalance/ProjectedBalance';
import { ProjectedCosts } from './ProjectedCosts/ProjectedCosts';
import SettingsPanel from './SettingsPanel';
import { GraphType } from './constants';
import { useProjectedCostsQuery } from '@/src/api';
import {
  ContractsResponse,
  CurrencyValueResponse,
  Pounds,
  ProjectedCostsResponse,
} from '@/src/types/Response';
import { recDdApproxEqualToCurrentDd } from '@/src/utils/shouldShowRecommendedDD';

import { formatFullDateWithoutOrdinal } from '@/src/utils/date';
import { formatPounds } from '@/src/utils/money';
import useWindowDimensions from '@/src/utils/hooks/useWindowDimensions';
import { isOnStandardDDCalculation } from '../utils';
import { useDebouncedValue } from '@/src/utils/hooks/useDebouncedValue';

interface DirectDebitCalculatorSettingsAndGraphProps {
  directDebitAmount: CurrencyValueResponse | undefined;
  balance: Pounds;
  contracts: ContractsResponse;
  newDirectDebitAmount: string;
  setNewDirectDebitAmount: (newValue: string) => void;
}

export const DirectDebitCalculatorSettingsAndGraph = memo(
  (props: DirectDebitCalculatorSettingsAndGraphProps) => {
    const {
      directDebitAmount,
      balance,
      contracts,
      newDirectDebitAmount,
      setNewDirectDebitAmount,
    } = props;

    const debouncedAmount = useDebouncedValue(newDirectDebitAmount, 1000);

    const { accountId } = useAccountContext();
    const { isDesktop } = useWindowDimensions({ debounceDelay: 0 });

    const [selectedGraphType, setSelectedGraphType] = useState(
      GraphType.MONTHLY_END_BALANCE,
    );

    const onAmountChange = useCallback(
      (event: FormEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;
        let newValue = value;
        if (Number(value) > 2000) {
          newValue = '2000.00';
        }

        // CurrencyField cannot be constrained to type="number", so need to handle non-numeric entries
        if (!isNaN(Number(value))) {
          setNewDirectDebitAmount(newValue);
        }
      },
      [setNewDirectDebitAmount],
    );

    const projectedCosts: UseQueryResult<ProjectedCostsResponse> =
      useProjectedCostsQuery(accountId);

    const onGraphTypeChange = useCallback(
      (type: GraphType) => setSelectedGraphType(type),
      [],
    );

    const amount = projectedCosts.data?.recommendedDirectDebit?.friendlyAmount;
    const minimumPayment = amount && formatPounds(amount);
    const calculationEndDate =
      projectedCosts.data &&
      formatFullDateWithoutOrdinal(
        projectedCosts.data.recommendedDirectDebit.calculationEndDate,
      );

    const isStandardDDCustomer = calculationEndDate
      ? isOnStandardDDCalculation(
          projectedCosts.data.recommendedDirectDebit.calculationEndDate.toString(),
        )
      : true;

    const ProjectedCostsContainer = () => {
      if (projectedCosts.isError) {
        return <ErrorPanel />;
      }
      if (projectedCosts.data == null || projectedCosts.isFetching) {
        return <LoadingPanel />;
      }

      const payingRightAmount =
        !!directDebitAmount &&
        recDdApproxEqualToCurrentDd(
          projectedCosts.data.recommendedDirectDebit.friendlyAmount,
          directDebitAmount,
        );

      return selectedGraphType === GraphType.MONTHLY_ESTIMATED_COST ? (
        <ProjectedCosts
          balance={{
            amount: balance.pounds,
            currencyUnit: 'GBP',
          }}
          projectedCosts={projectedCosts.data}
          newDirectDebitAmount={debouncedAmount}
          payingRightAmount={payingRightAmount}
          calculationEndDate={
            projectedCosts.data.recommendedDirectDebit.calculationEndDate
          }
          isStandardDDCustomer={isStandardDDCustomer}
          selectedGraphType={selectedGraphType}
          handleGraphTypeChange={onGraphTypeChange}
          contracts={contracts}
        />
      ) : (
        <ProjectedBalance
          balance={{
            amount: balance.pounds,
            currencyUnit: 'GBP',
          }}
          newDirectDebitAmount={debouncedAmount}
          projectedCosts={projectedCosts.data}
          payingRightAmount={payingRightAmount}
          calculationEndDate={
            projectedCosts.data.recommendedDirectDebit.calculationEndDate
          }
          isStandardDDCustomer={isStandardDDCustomer}
          selectedGraphType={selectedGraphType}
          handleGraphTypeChange={onGraphTypeChange}
          contracts={contracts}
        />
      );
    };

    return (
      <>
        <Margin bottom={6}>
          <Card>
            <Heading1>Direct Debit calculator</Heading1>
            {isStandardDDCustomer ? (
              <P>
                Your monthly minimum payment is {minimumPayment}. The aim is to
                make sure you have credit for 1 month’s payment in your OVO
                account by {calculationEndDate}.
              </P>
            ) : (
              <P>
                Your monthly minimum payment is {minimumPayment}. The aim is to
                make sure there’s no balance left for you to pay in your OVO
                account by {calculationEndDate}.
              </P>
            )}
            <P>
              Use our tool to see how changing your payment affects your end of
              year balance.
            </P>
          </Card>
        </Margin>
        <Row isNested>
          <Col large={4} medium={4}>
            <SettingsPanel
              newDirectDebitAmount={newDirectDebitAmount}
              contracts={contracts}
              handleAmountChange={onAmountChange}
              projectedCosts={projectedCosts.data!}
              isStandardDDCustomer={isStandardDDCustomer}
            />
          </Col>
          <Col large={8} medium={8}>
            <Margin bottom={6}>
              <ProjectedCostsContainer />
            </Margin>
          </Col>
        </Row>
        <Margin bottom={6}>
          <P>
            {!isDesktop
              ? projectedCosts.data &&
                (isStandardDDCustomer
                  ? `The amount predicted by the calculator might change based on your actual energy use costs. To help cover any changes over the course of the year, your Direct Debit payments are calculated to make sure you have credit for 1 month’s payment in your OVO account by ${calculationEndDate}.`
                  : 'The amount predicted by the calculator might change based on your actual energy use costs.')
              : null}
          </P>
        </Margin>
      </>
    );
  },
);

DirectDebitCalculatorSettingsAndGraph.displayName =
  'DirectDebitCalculatorSettingsAndGraph';
